/**
 * Install prompt (FE-04 FR-FE04-14/15). `beforeinstallprompt` is captured globally from app start
 * (`captureInstallPrompt()` in main.tsx) and kept in memory; `useInstallPrompt()` exposes
 * `canInstall`, `install()`, `isInstalled` and, on iOS Safari outside standalone, `iosInstructions`.
 */
import { useSyncExternalStore } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform?: string }>;
}

interface InstallState {
  event: BeforeInstallPromptEvent | null;
  installed: boolean;
}

let state: InstallState = { event: null, installed: false };
const listeners = new Set<() => void>();
let captured = false;

function set(next: Partial<InstallState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  return typeof window.matchMedia === "function"
    ? window.matchMedia("(display-mode: standalone)").matches
    : false;
}

/** iPhone / iPad Safari (iPadOS reports a Mac with touch); other iOS browsers can't install either. */
export function isIos(userAgent = navigator.userAgent, maxTouchPoints = navigator.maxTouchPoints) {
  return /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
}

/** Start listening (idempotent). Call once at app start so an early event is not lost. */
export function captureInstallPrompt(): void {
  if (captured || typeof window === "undefined") return;
  captured = true;
  set({ installed: isStandalone() });
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    set({ event: event as BeforeInstallPromptEvent });
  });
  window.addEventListener("appinstalled", () => set({ event: null, installed: true }));
}

/** Show the browser's install dialog; resolves with the user's choice. */
export async function install(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const event = state.event;
  if (!event) return "unavailable";
  set({ event: null });
  await event.prompt();
  const choice = await event.userChoice;
  if (choice.outcome === "accepted") set({ installed: true });
  return choice.outcome;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const snapshot = () => state;

export interface InstallPrompt {
  canInstall: boolean;
  isInstalled: boolean;
  /** iOS Safari outside standalone: show "Compartir → Agregar a inicio" instead (FR-FE04-15). */
  iosInstructions: boolean;
  install: typeof install;
}

export function useInstallPrompt(): InstallPrompt {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  const installed = current.installed || isStandalone();
  return {
    canInstall: !installed && current.event !== null,
    isInstalled: installed,
    iosInstructions: !installed && isIos(),
    install,
  };
}

/** Test helper. */
export function resetInstallPromptForTests(): void {
  captured = false;
  state = { event: null, installed: false };
}
